import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { IWard } from "../../../@types/admin/AdminWard";
import { TableSorted } from "../../../components/TableSorted";
import { useWards } from "../../../hooks/queries/admin/wards";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FilterButton,
  FiltersContainer,
  FiltersActions,
  PageContainer,
  PageHeader,
  PageTitle,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { AddWardModal } from "./AddWardModal";

const SEARCH_FIELDS: (keyof IWard)[] = ["id", "code", "name", "groupName"];

export function AdminWardsList() {
  document.title = "Wards - Admin";

  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { wards, isLoading, isError } = useWards();

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: wards, searchFields: SEARCH_FIELDS });

  const handleAddClick = () => {
    openModal(<AddWardModal onClose={closeModal} />, {
      width: "480px",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<IWard, string>[] = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => <span>{row.original.id}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "code",
        header: "Code",
        cell: ({ row }) => <span>{row.original.code || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "name",
        header: "Ward",
        cell: ({ row }) => <span>{row.original.name}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "groupName",
        header: "Hospital",
        cell: ({ row }) => <span>{row.original.groupName || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "publish",
        header: "Publish",
        // TableSorted renders "-" and skips the cell renderer whenever the
        // column's value is falsy — which covers `false` and any column with
        // no accessor at all. The label doubles as the sort key.
        accessorFn: (ward) => (ward.publish ? "Published" : "Unpublished"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.publish ? "active" : "inactive"}>
            {row.original.publish ? "Published" : "Unpublished"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Wards</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by code, ward, or hospital..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FiltersActions>
          <FilterButton variant="primary" onClick={handleAddClick}>
            Add ward
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the wards. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-wards"]]}>
          <TableSorted
            columns={columns}
            data={items}
            onRowClick={(ward: IWard) => navigate(`/admin/wards/${ward.id}`)}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "wards",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
